import { countWords, parseBlogBlocks, plainText } from '@/modules/marketing/blog/blog-markdown';
import type { BlogBlock } from '@/modules/marketing/blog/blog-markdown';

/**
 * The blog's posts, read from `posts/*.md` at build time.
 *
 * ## One file per post, and the build refuses a broken one
 *
 * A post is a Markdown file with a small front matter block. Everything a search result, a link
 * preview and the index need comes from that block, so there is no second list to keep in step with
 * the files, which is the arrangement the feature articles went wrong with.
 *
 * Anything that would publish a page nobody meant to publish throws here, while the site is being
 * built, rather than being found in a search result: a description that is the wrong length, a
 * related post that does not exist, a call to action that points off the site, or the dash the
 * owner reads as a placeholder.
 *
 * ## Two images per post, by convention
 *
 * `public/blog/<slug>.svg` is the illustration the page and the index show, and
 * `public/blog/<slug>-og.jpg` is the 1200 by 630 card a link preview shows. Neither is named in the
 * front matter, so a post cannot point at an image that is not there under a name nobody can
 * guess. `frontend/scripts/blog-art/` holds how both are made.
 */

export const BLOG_TOPICS = ['Backups', 'Monitoring', 'Automation', 'Security', 'Operations'] as const;
export type BlogTopic = (typeof BLOG_TOPICS)[number];

export interface BlogPost {
  slug: string;
  title: string;
  /** The `<title>` without the site name, when it should say more than the heading does. */
  seoTitle: string;
  description: string;
  topic: BlogTopic;
  /** `YYYY-MM-DD`. Never moved to look fresh: it is the day the post was published. */
  published: string;
  /** `YYYY-MM-DD`. Moves only when the post's substance changes. */
  modified: string;
  keywords: string[];
  author: { name: string; url: string | null };
  image: { src: string; ogSrc: string; alt: string; width: number; height: number };
  blocks: BlogBlock[];
  /** Words of prose, not counting commands. */
  words: number;
  minutes: number;
  /** Slugs of the posts that continue this one. */
  related: string[];
  /** The closing box: the one place a post asks for anything. */
  cta: { heading: string; text: string; label: string; to: string };
}

const RAW = import.meta.glob<string>('./posts/*.md', { eager: true, query: '?raw', import: 'default' });

/** Both dashes, because the owner reads either one as a placeholder. See the website copy rules. */
const FORBIDDEN = /[–—]/;

function frontMatter(file: string, raw: string): { meta: Record<string, string>; body: string } {
  const text = raw.replace(/\r\n/g, '\n');
  const match = /^---\n([\s\S]*?)\n---\n?/.exec(text);

  if (match === null) {
    throw new Error(`${file}: a post starts with a front matter block between two --- lines.`);
  }

  const meta: Record<string, string> = {};

  for (const line of (match[1] ?? '').split('\n')) {
    const colon = line.indexOf(':');

    if (colon > 0) {
      meta[line.slice(0, colon).trim()] = line.slice(colon + 1).trim();
    }
  }

  return { meta, body: text.slice(match[0].length) };
}

function required(file: string, meta: Record<string, string>, key: string): string {
  const value = meta[key];

  if (value === undefined || value === '') {
    throw new Error(`${file}: front matter is missing "${key}".`);
  }

  return value;
}

function load(): BlogPost[] {
  const posts: BlogPost[] = [];
  const order = new Map<string, number>();

  for (const [path, raw] of Object.entries(RAW)) {
    const file = path.split('/').pop() ?? path;
    const slug = file.replace(/\.md$/, '');
    const { meta, body } = frontMatter(file, raw);

    if (FORBIDDEN.test(raw)) {
      throw new Error(`${file}: contains an en dash or an em dash. Use a comma, a colon or a full stop.`);
    }

    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
      throw new Error(`${file}: the file name is the URL, so it is lowercase words joined by hyphens.`);
    }

    const title = required(file, meta, 'title');
    const description = required(file, meta, 'description');
    const topic = required(file, meta, 'topic') as BlogTopic;
    const published = required(file, meta, 'date');
    const modified = meta['modified'] ?? published;

    if (!(BLOG_TOPICS as readonly string[]).includes(topic)) {
      throw new Error(`${file}: topic "${topic}" is not one of ${BLOG_TOPICS.join(', ')}.`);
    }

    for (const date of [published, modified]) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(`${date}T00:00:00Z`))) {
        throw new Error(`${file}: "${date}" is not a YYYY-MM-DD date.`);
      }
    }

    if (description.length < 120 || description.length > 160) {
      throw new Error(`${file}: the description is ${description.length} characters; a snippet reads best at 120 to 160.`);
    }

    if (title.length > 60) {
      throw new Error(`${file}: the title is ${title.length} characters; keep a heading to 60 or fewer.`);
    }

    const blocks = parseBlogBlocks(body);
    const words = countWords(blocks);
    const cta = {
      heading: required(file, meta, 'cta_heading'),
      text: required(file, meta, 'cta_text'),
      label: required(file, meta, 'cta_label'),
      to: required(file, meta, 'cta_to'),
    };

    if (!cta.to.startsWith('/') || cta.to.startsWith('//')) {
      throw new Error(`${file}: cta_to is a path on this site, not "${cta.to}".`);
    }

    for (const block of blocks) {
      if (block.type === 'heading' && /[`*[\]]/.test(block.text)) {
        throw new Error(`${file}: the heading "${block.text}" has Markdown in it; headings are plain text.`);
      }
    }

    order.set(slug, Number(meta['order'] ?? '99'));

    posts.push({
      slug,
      title,
      seoTitle: meta['seo_title'] ?? title,
      description,
      topic,
      published,
      modified,
      keywords: required(file, meta, 'keywords')
        .split(',')
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword !== ''),
      author: { name: meta['author'] ?? 'Techie Gain', url: meta['author_url'] ?? null },
      image: {
        src: `/blog/${slug}.svg`,
        ogSrc: `/blog/${slug}-og.jpg`,
        alt: required(file, meta, 'image_alt'),
        width: 1600,
        height: 900,
      },
      blocks,
      words: words.prose,
      minutes: Math.max(1, Math.round(words.prose / 200)),
      related: required(file, meta, 'related')
        .split(',')
        .map((related) => related.trim())
        .filter((related) => related !== ''),
      cta,
    });
  }

  const slugs = new Set(posts.map((post) => post.slug));

  for (const post of posts) {
    for (const related of post.related) {
      if (related === post.slug || !slugs.has(related)) {
        throw new Error(`${post.slug}.md: related post "${related}" is not another post.`);
      }
    }

    if (plainText(post.description).includes('  ')) {
      throw new Error(`${post.slug}.md: the description has a double space.`);
    }
  }

  // Newest first, and the front matter's `order` within a day, so a batch published together
  // reads in the sequence it was meant to rather than alphabetically.
  return posts.sort(
    (a, b) =>
      b.published.localeCompare(a.published) || (order.get(a.slug) ?? 99) - (order.get(b.slug) ?? 99),
  );
}

export const POSTS: BlogPost[] = load();

export function postBySlug(slug: string | undefined): BlogPost | undefined {
  return POSTS.find((post) => post.slug === slug);
}

export function relatedPosts(post: BlogPost): BlogPost[] {
  return post.related
    .map((slug) => postBySlug(slug))
    .filter((related): related is BlogPost => related !== undefined);
}

/**
 * "October 12, 2026", in UTC.
 *
 * Fixed to one zone because the same function runs in the build and in a visitor's browser, and a
 * date that reads one way in the pre-rendered HTML and another after the app mounts is a flicker.
 */
export function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
